import React, { useEffect } from "react";
import { usePlayerSessions } from "@/hooks/usePlayerSessions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PurchasedMentorshipCard } from "@/components/player/PurchasedMentorshipCard";
import { EmptyState } from "@/components/player/EmptyState";
import { ImmediatePaymentProcessor } from "@/components/admin/ImmediatePaymentProcessor";

const MentoriasCompradas = () => {
  const { sessions, loading, refetch } = usePlayerSessions();

  useEffect(() => {
    // Log sessions data for debugging
    if (sessions.length > 0) {
      console.log("Sessions loaded in MentoriasCompradas:", sessions);
    }
  }, [sessions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  const upcomingSessions = sessions
    .filter((session) => 
      session.status === "confirmado" || session.status === "pendente"
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const completedSessions = sessions
    .filter((session) => 
      session.status === "concluido"
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Mentorias Compradas</h1>
          <p className="text-gray-400 mt-1">
            Gerencie suas mentorias e sessões agendadas
          </p>
        </div>
        <Button 
          variant="outline" 
          size="icon"
          onClick={() => refetch(true)}
          className="h-9 w-9 self-start sm:self-auto"
          title="Atualizar mentorias"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Componente de processamento imediato - apenas se não há mentorias */}
      {sessions.length === 0 && (
        <div className="mb-8">
          <ImmediatePaymentProcessor />
        </div>
      )}

      {sessions.length === 0 ? (
        <EmptyState 
          title="Nenhuma mentoria comprada" 
          description="Você ainda não comprou nenhuma mentoria. Explore mentores e jogos para encontrar a mentoria ideal para você."
          actionLabel="Explorar Mentorias"
          actionHref="/mentorias"
        />
      ) : (
        <>
          {/* Upcoming Sessions */}
          <Card className="bg-[#1A2233] border-[#222A35] text-white mb-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Próximas Sessões</CardTitle>
              {upcomingSessions.length > 0 && (
                <Badge variant="default" className="bg-[#00B6F0]">
                  {upcomingSessions.length}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {/* Instrução para o jogador */}
              <div className="mb-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-500 rounded-full p-1 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-blue-400 font-medium text-sm mb-1">Próximos passos</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Depois de confirmado o pagamento, o próximo passo é o mentor adicionar o link da mentoria, 
                      basta aguardar ou se quiser falar com ele, clique no botão falar com mentor.
                    </p>
                  </div>
                </div>
              </div>

              {upcomingSessions.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {upcomingSessions.map((session) => (
                    <PurchasedMentorshipCard 
                      key={session.id} 
                      session={session} 
                      onActionCompleted={refetch}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-gray-400">Nenhuma sessão agendada</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Past Sessions */}
          <Card className="bg-[#1A2233] border-[#222A35] text-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Sessões Concluídas</CardTitle>
              {completedSessions.length > 0 && (
                <Badge variant="outline">
                  {completedSessions.length}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {completedSessions.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {completedSessions.map((session) => (
                    <PurchasedMentorshipCard 
                      key={session.id} 
                      session={session} 
                      onActionCompleted={refetch}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-gray-400">Nenhuma sessão concluída</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default MentoriasCompradas;
